import {makeAutoObservable, observable} from 'mobx';
import {uploadStudioVideo} from '../../../videoUpload';
import {
  BrowseDocument,
  LibraryFile,
  SourceKind,
  SourceType,
  isMp4LibraryFile,
  isMp4Upload,
  isPdfName,
} from '../createOptions';
import {libraryFileProblem, uploadProblem} from './sourceChecks';
import type CreateStore from './CreateStore';

type PrivateFields = 'page' | 'visit' | 'uploadAbort' | 'uploadedVideo';

/**
 * What the microreels are made from: a prompt, or a document or video, dropped on the page or
 * chosen from the library. A picked video goes to the library when the job is created, once.
 *
 * The page's form decides whether videos are taken and holds the error and title a choice
 * changes. A new visit drops the answers of the previous one (a library pick, an upload).
 */
class SourceStore {
  private visit = 0;
  private uploadAbort: AbortController | null = null;
  // A video already sent to the library is reused if creating the job fails.
  private uploadedVideo: {file: File; record: LibraryFile} | null = null;

  public type: SourceType = 'prompt';
  public file: File | null = null;
  public libraryFile: LibraryFile | null = null;
  // A PDF upload stays in the browser until the job is created, so its preview
  // reads an object URL.
  public upload: {file: File; url: string} | null = null;
  public excluded = '';
  public uploading = false;

  constructor(private readonly page: CreateStore) {
    makeAutoObservable<SourceStore, PrivateFields>(
      this,
      {
        page: false,
        visit: false,
        uploadAbort: false,
        uploadedVideo: false,
        file: observable.ref,
        libraryFile: observable.ref,
        upload: observable.ref,
      },
      {autoBind: true},
    );
  }

  public get isDocument() {
    return this.type === 'document';
  }

  public get hasFile() {
    return !!this.file || !!this.libraryFile;
  }

  /** The chosen file is a video, on a page that takes videos. */
  public get video() {
    return (
      this.page.videos &&
      this.isDocument &&
      (this.file
        ? isMp4Upload(this.file)
        : !!this.libraryFile && isMp4LibraryFile(this.libraryFile))
    );
  }

  public get kind(): SourceKind {
    return this.video ? 'video' : 'document';
  }

  /** A replaced upload's URL is never shown under the new file's name. */
  public get uploadUrl() {
    return this.upload?.file === this.file ? this.upload.url : null;
  }

  public setType(type: SourceType) {
    this.type = type;
  }

  public pickFile(file?: File) {
    // A file dropped while the microreel is being created would not be the one it is made from.
    if (!file || this.page.busy) return;
    const problem = uploadProblem(file, this.page);
    if (problem) return this.page.report(problem);
    this.page.report(null);
    this.setFiles(file, null);
    this.page.fileChosen(file.name);
  }

  /** Opens the library; cancelling it keeps the current source. */
  public async browseLibrary(browse?: BrowseDocument) {
    const visit = this.visit;
    const selected = await browse?.({videos: this.page.videos});
    if (selected && this.visit === visit) this.takeLibraryFile(selected);
  }

  public takeLibraryFile(selected: LibraryFile) {
    const problem = libraryFileProblem(selected, this.page);
    if (problem) return this.page.report(problem);
    this.setFiles(null, selected);
    this.page.report(null);
    this.page.fileChosen(selected.fileName);
  }

  public removeFile() {
    this.setFiles(null, null);
  }

  public setExcluded(excluded: string) {
    this.excluded = excluded;
  }

  /**
   * The picked video in the library: uploaded to the organization's library the first time,
   * and reused when creating the job failed. Null once the visit has ended.
   */
  public async uploadVideo(
    organizationId: string,
    file: File,
  ): Promise<LibraryFile | null> {
    if (this.uploadedVideo?.file === file) return this.uploadedVideo.record;
    const visit = this.visit;
    this.setUploading(true);
    this.uploadAbort = new AbortController();
    const record = await uploadStudioVideo(
      organizationId,
      file,
      this.uploadAbort.signal,
    );
    if (this.visit !== visit) return null;
    this.uploaded(file, record);
    return record;
  }

  public setUploading(uploading: boolean) {
    this.uploading = uploading;
  }

  /** Starts a new visit: an upload under way stops, and the source is empty again. */
  public reset() {
    this.visit++;
    this.uploadAbort?.abort();
    this.uploadAbort = null;
    this.uploadedVideo = null;
    this.revokeUpload();
    this.type = 'prompt';
    this.file = null;
    this.libraryFile = null;
    this.excluded = '';
    this.uploading = false;
  }

  private uploaded(file: File, record: LibraryFile) {
    this.uploadedVideo = {file, record};
    this.uploading = false;
  }

  private setFiles(file: File | null, libraryFile: LibraryFile | null) {
    if (file !== this.file) {
      this.revokeUpload();
      if (file && isPdfName(file.name))
        this.upload = {file, url: URL.createObjectURL(file)};
    }
    this.file = file;
    this.libraryFile = libraryFile;
  }

  private revokeUpload() {
    if (this.upload) URL.revokeObjectURL(this.upload.url);
    this.upload = null;
  }
}

export default SourceStore;
